import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
}
function FAQItem({
  question,
  answer
}: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden shadow-sm" data-id="element-254">
      <button className="flex justify-between items-center w-full text-left p-5 bg-white hover:bg-gray-50 transition-colors focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-255">
        <h3 className="text-xl font-medium text-gray-800" data-id="element-256">{question}</h3>
        <span className="text-teal-600" data-id="element-257">
          {isOpen ? <ChevronUpIcon size={24} data-id="element-258" /> : <ChevronDownIcon size={24} data-id="element-259" />}
        </span>
      </button>
      {isOpen && <div className="p-5 bg-white border-t border-gray-200" data-id="element-260">
          <div className="text-lg text-gray-700 font-medium" data-id="element-261">{answer}</div>
        </div>}
    </div>;
}
export function FAQ() {
  return <div className="w-full my-10" data-id="element-262">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center" data-id="element-263">
        Still Have Questions? Let's Clear Them Up.
      </h2>
      <p className="text-xl text-gray-700 mb-8 text-center font-medium" data-id="element-264">
        I know how big this decision can feel — especially when you've already
        tried so many things.
        <br data-id="element-265" />
        So let me walk you through some common questions that might be on your
        mind:
      </p>
      <div className="space-y-4" data-id="element-266">
        <FAQItem question="What exactly is the Neurotoned Method?" answer={<p className="mb-4" data-id="element-268">
              The Neurotoned Method is a simple, step-by-step program that helps
              you calm your nervous system using gentle daily body-based tools.
              <br data-id="element-269" />
              <br data-id="element-270" />
              It focuses on strengthening your vagus nerve — the part of your
              body that controls how safe and calm you feel.
              <br data-id="element-271" />
              <br data-id="element-272" />
              You'll get guided videos, calming audio tools, and short, easy
              exercises that help you stop spirals and feel more grounded each
              day.
            </p>} data-id="element-267" />
        <FAQItem question="Who is this for?" answer={<>
              <p className="mb-4" data-id="element-274">This is for you if:</p>
              <ul className="list-disc pl-6 mb-4" data-id="element-275">
                <li className="mb-2" data-id="element-276">
                  You struggle with health anxiety, spiraling thoughts, or panic
                  symptoms
                </li>
                <li className="mb-2" data-id="element-277">
                  You're tired of feeling like your body is always "on edge"
                </li>
                <li className="mb-2" data-id="element-278">
                  You've tried therapy, supplements, or mindset work — but still
                  feel stuck
                </li>
                <li className="mb-2" data-id="element-279">
                  You want a gentle, non-triggering way to feel calm again
                </li>
              </ul>
              <p data-id="element-280">
                You don't need to be "good at yoga" or "spiritual" or have any
                special skills. Just a willingness to try.
              </p>
            </>} data-id="element-273" />
        <FAQItem question="Do I need a doctor's approval to do this?" answer={<p className="mb-4" data-id="element-282">
              No. The Neurotoned Method is 100% natural and non-medical.
              <br data-id="element-283" />
              <br data-id="element-284" />
              It's designed to be safe, supportive, and gentle — even for people
              with trauma or chronic stress.
              <br data-id="element-285" />
              <br data-id="element-286" />
              If you're under care for something specific, feel free to talk
              with your provider. But most people use this alongside their
              current care with no issues at all.
            </p>} data-id="element-281" />
        <FAQItem question="How long does it take each day?" answer={<p className="mb-4" data-id="element-288">
              Just 7 minutes a day.
              <br data-id="element-289" />
              <br data-id="element-290" />
              You can do more if you want, but the method was designed to be
              simple and easy to stick with — even on hard days.
            </p>} data-id="element-287" />
        <FAQItem question="What if I get too overwhelmed to do it?" answer={<p className="mb-4" data-id="element-292">
              That's okay. This program was built for those exact moments.
              <br data-id="element-293" />
              <br data-id="element-294" />
              You can lie down. You can pause. You can come back to it anytime.
              <br data-id="element-295" />
              <br data-id="element-296" />
              There's no wrong way to use the tools. Just press play and let
              your body guide you.
            </p>} data-id="element-291" />
        <FAQItem question="What happens after I buy?" answer={<>
              <p className="mb-4" data-id="element-298">
                As soon as you complete your order, you'll get instant access
                to:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-299">
                <li className="mb-2" data-id="element-300">All the daily reset videos</li>
                <li className="mb-2" data-id="element-301">Calming tools and audio sessions</li>
                <li className="mb-2" data-id="element-302">
                  A private member area to track your journey
                </li>
                <li className="mb-2" data-id="element-303">Our supportive online community</li>
              </ul>
              <p data-id="element-304">
                You'll also receive an email with your login details so you can
                come back anytime.
              </p>
            </>} data-id="element-297" />
        <FAQItem question="What if it doesn't work for me?" answer={<p className="mb-4" data-id="element-306">
              That's okay too.
              <br data-id="element-307" />
              <br data-id="element-308" />
              You're backed by a 30-day money-back guarantee. Try it. Use the
              tools. Feel it in your body.
              <br data-id="element-309" />
              <br data-id="element-310" />
              And if you don't notice a shift — or if it just doesn't feel right
              — send us a message and we'll refund you. No pressure. No stress.
            </p>} data-id="element-305" />
        <FAQItem question="Why should I join today?" answer={<p className="mb-4" data-id="element-312">
              Because your nervous system is already asking for help.
              <br data-id="element-313" />
              <br data-id="element-314" />
              And right now, we have a limited number of open spots so we can
              support each member well.
              <br data-id="element-315" />
              <br data-id="element-316" />
              We only open access in waves — and once it fills up, we close
              enrollment until the next cycle.
              <br data-id="element-317" />
              <br data-id="element-318" />
              So if you're feeling the pull… don't wait.
            </p>} data-id="element-311" />
      </div>
    </div>;
}